/**
 * Relative cost-category pill (#1844 Slice 2) — Low / Medium / High / Premium,
 * or "—" for an unpriced model. Shared by the Pricing table and the assignment
 * picker (both the selected-model chip AND each dropdown option) so the styling
 * matches everywhere.
 *
 * NEVER shows a dollar figure — the whole point of the category is an
 * audience-neutral cost signal (staff now, end users on any plan later, who see
 * AI credits, not dollars). The visual scale is drawn in the **credits glyph
 * (✦)**, not "$": one ✦ for Low up to four for Premium, with the remaining
 * glyphs greyed so the reader sees the scale is out of four. We deliberately
 * did NOT use "$/$$/$$$" — a dollar sign would re-introduce the currency the
 * credits model exists to replace, and read as "billed in dollars" on a
 * credits product. The word carries the meaning for screen readers; the ✦ run
 * is decorative (`aria-hidden`).
 */
import type { ModelPricingRow } from '@/lib/api'

type CostCategory = NonNullable<ModelPricingRow['costCategory']>

/** Cheapest → priciest. Load-bearing order — mirrors the server's
 * `COST_CATEGORIES` (no shared package) and drives the table sort. */
export const COST_CATEGORY_ORDER: readonly CostCategory[] = ['low', 'medium', 'high', 'premium']

/** Exported so an interactive control can wear the same words as the pill
 * (#2011). A control that restated them would drift the moment one side was
 * reworded, and the whole point of the category is that "Premium" means one
 * thing everywhere the operator sees it. */
export const COST_CATEGORY_LABEL: Record<CostCategory, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  premium: 'Premium',
}

/** Exported alongside the labels, for the same reason (#2011). Selected state
 * on the cost toggles IS this pill's colour — that is what tells the operator
 * the toggle and the column are talking about the same thing. */
export const COST_CATEGORY_STYLE: Record<CostCategory, string> = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-amber-100 text-amber-800',
  high: 'bg-orange-100 text-orange-800',
  premium: 'bg-indigo-100 text-indigo-800',
}

/** How many filled credit glyphs each category shows (out of four). */
const FILLED: Record<CostCategory, number> = { low: 1, medium: 2, high: 3, premium: 4 }

/**
 * The ✦ run for a category: filled glyphs up to its level, the rest greyed so
 * the reader sees the scale is out of four. Decorative — the word carries the
 * meaning for screen readers, so this is always `aria-hidden`.
 *
 * Exported (#2011) so the cost toggles draw the identical scale rather than
 * re-deriving which glyphs are filled.
 */
export function CostCategoryGlyphs({ category }: { category: CostCategory }) {
  const filled = FILLED[category]
  return (
    <span aria-hidden="true" className="font-mono leading-none tracking-tight">
      {CREDIT_GLYPH.repeat(filled)}
      <span className="opacity-30">{CREDIT_GLYPH.repeat(COST_CATEGORY_ORDER.length - filled)}</span>
    </span>
  )
}

/** The credits glyph used for the scale — the same mark the app shows for AI
 * credits elsewhere, so the cost signal reads in the customer's own currency. */
const CREDIT_GLYPH = '✦'

/** Sort index for a category; an absent (unpriced) category sorts LAST. */
export function costCategoryRank(category?: CostCategory): number {
  return category ? COST_CATEGORY_ORDER.indexOf(category) : COST_CATEGORY_ORDER.length
}

/**
 * @param category the model's cost category, or undefined (unpriced) → "—".
 */
export function CostCategoryPill({ category }: { category?: CostCategory }) {
  if (!category) return <span className="text-muted-foreground">—</span>
  return (
    <span
      title={`Relative cost — ${COST_CATEGORY_LABEL[category]}`}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${COST_CATEGORY_STYLE[category]}`}
    >
      {/* Word in its own text node so the label is findable on its own and the
          decorative glyph run below doesn't get concatenated into it. */}
      <span>{COST_CATEGORY_LABEL[category]}</span>
      <CostCategoryGlyphs category={category} />
    </span>
  )
}
